import React, { useState } from 'react';
import { FileText, Search, Book, Code, Shield, RefreshCw, ArrowRight, Download, Copy, Check } from 'lucide-react';

// Déclarer toutes les variables avant leur utilisation
const payinRequest = {
  "merchant_id": "xxxxxxxxxxxxxxxxxxxxxx",
  "merchant_secrete": "xxxxxxxxxxxxxxxxxxxxxx",
  "amount": "100",
  "currency": "CDF",
  "action": "debit",
  "customer_number": "0972148867",
  "firstname": "John",
  "lastname": "Doe",
  "e-mail": "john.doe@example.com",
  "reference": "testfp09",
  "method": "airtel",
  "callback_url": "https://yourdomain.com/callback"
};

const payinResponse = {
  "Amount": 100,
  "Comment": "Transaction Received Successfully",
  "Created_At": "2024-03-08 08:08:51.533410",
  "Currency": "CDF",
  "Customer_Number": "972148867",
  "Reference": "testfp09",
  "Status": "Success",
  "Transaction_id": "PDABXKT031fD08M9PfR24C14",
  "Updated_At": "2024-03-08 08:08:51.533410"
};

const payoutRequest = {
  "merchant_id": "xxxxxxxxxxxxxxxxxxxxxx",
  "merchant_secrete": "xxxxxxxxxxxxxxxxxxxxxx",
  "amount": "100",
  "currency": "CDF",
  "action": "credit",
  "customer_number": "0972148867",
  "firstname": "Jane",
  "lastname": "Doe",
  "e-mail": "jane.doe@example.com",
  "reference": "testfp09",
  "method": "airtel",
  "callback_url": "https://yourdomain.com/callback"
};

const payoutResponse = {
  "Amount": 100,
  "Comment": "Transaction Received Successfully",
  "Created_At": "2024-03-08 08:08:51.533410",
  "Currency": "CDF",
  "Customer_Number": "972148867",
  "Reference": "testfp09",
  "Status": "Success",
  "Transaction_id": "PDABXKT031fD08M9PfR24C14",
  "Updated_At": "2024-03-08 08:08:51.533410"
};

const checkStatusRequest = {
  "merchant_id": "xxxxxxxxxxxxxxxxxxxxxx",
  "merchant_secrete": "xxxxxxxxxxxxxxxxxxxxxx",
  "action": "verify",
  "reference": "PDYjZJL10X8J26VoQmM23bHV"
};

const checkStatusResponse = {
  "Action": "debit",
  "Amount": 100.0,
  "Comment": "Transaction Found",
  "Created_at": "2024-03-08 08:05:37",
  "Currency": "CDF",
  "Customer_Details": "972148867",
  "Financial_Institution_id": "null",
  "Method": "airtel",
  "Reference": "testfp09",
  "Status": "Success",
  "Trans_Status": "Failed",
  "Trans_Status_Description": "La reference de la transaction est invalide, veuillez resrayez ou contactez le service client au 1213",
  "Transaction_id": "PD9pLLM030ZJ08X7fXM242x6",
  "Updated_at": "2024-03-08 08:51:30"
};

const codeExamples = [
  {
    language: "Python",
    code: `import json
import base64
import hashlib
import hmac
from Crypto.Cipher import AES
from Crypto.Util.Padding import unpad
from flask import Flask, request, jsonify

SECRET_KEY = b'xxxxxxxxxxxxxxxx' # 16-byte AES key
HMAC_KEY = b'xxxxxxxxxxxxxxxx'

app = Flask(__name__)

def decrypt_data(encrypted_data):
    cipher = AES.new(SECRET_KEY, AES.MODE_CBC, iv=SECRET_KEY)
    decrypted_bytes = unpad(cipher.decrypt(base64.b64decode(encrypted_data)), AES.block_size)
    return json.loads(decrypted_bytes.decode())

def verify_signature(encrypted_message, received_signature):
    calculated_signature = hmac.new(HMAC_KEY, encrypted_message.encode(), hashlib.sha256).hexdigest()
    return hmac.compare_digest(calculated_signature, received_signature)

@app.route('/callback', methods=['POST'])
def callback_handler():
    data = request.json.get("data")
    received_signature = request.headers.get("X-Signature")

    if not received_signature:
        return jsonify({"error": "Signature missing"}), 400

    if verify_signature(data, received_signature):
        try:
            decrypted_data = decrypt_data(data)
            return jsonify({"status": "Callback received", "data": decrypted_data}), 200
        except Exception:
            return jsonify({"error": "Invalid encryption"}), 400
    else:
        return jsonify({"error": "Invalid signature"}), 401`
  },
  {
    language: "Node.js",
    code: `const express = require('express');
const crypto = require('crypto');
const base64 = require('base-64');
const bodyParser = require('body-parser');

const app = express();
app.use(bodyParser.json());

const SECRET_KEY = Buffer.from('xxxxxxxxxxxxxxx'); // 16-byte AES key
const HMAC_KEY = 'xxxxxxxxxxxxxxx';

function decryptData(encryptedData) {
    const decipher = crypto.createDecipheriv('aes-128-cbc', SECRET_KEY, SECRET_KEY);
    let decrypted = decipher.update(base64.decode(encryptedData), 'base64', 'utf8');
    decrypted += decipher.final('utf8');
    return JSON.parse(decrypted);
}

function verifySignature(encryptedMessage, receivedSignature) {
    const hmac = crypto.createHmac('sha256', HMAC_KEY);
    hmac.update(encryptedMessage);
    const calculatedSignature = hmac.digest('hex');
    return calculatedSignature === receivedSignature;
}

app.post('/callback', (req, res) => {
    const data = req.body.data;
    const receivedSignature = req.headers['x-signature'];
    
    if (!receivedSignature) {
        return res.status(400).json({ error: "Signature missing" });
    }
    
    if (verifySignature(data, receivedSignature)) {
        try {
            const decryptedData = decryptData(data);
            return res.status(200).json({ status: "Callback received", data: decryptedData });
        } catch (error) {
            return res.status(400).json({ error: "Invalid encryption" });
        }
    } else {
        return res.status(401).json({ error: "Invalid signature" });
    }
});`
  }
];

const DocumentationPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, language: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(language);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const documentationSections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: <Book className="h-5 w-5" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            This addendum provides specific event calls to be issued against the PayDRC Processor. It outlines the
            methods and policies for interacting with the Moko AfrikaPayment Switch for send money and payment
            transactions.
          </p>
          <p className="text-gray-700 leading-relaxed">
            The PayDRC Processor interfaces with the Moko AfrikaSwitch, which also interfaces with gateways and
            systems managing a customer's wallet. This wallet, in the case of mobile network operators, is the
            "Mobile Money Platform", or in the case of banks, the "cards platforms". It is physically separated from
            the payment gateway, sometimes referred to as a "Broker" or an "Orchestration Layer", providing the
            benefit of a simplified interface and abstraction of both function and security.
          </p>
          <p className="text-gray-700 leading-relaxed">
            This document provides detailed usage of the PayDRC processor API, allowing developers and third-party
            plugins to send requests to the Moko Afrikaswitch engine to debit a Moko Afrikacustomer account (C2B) or
            credit a Moko Afrikacustomer account (B2C).
          </p>
        </div>
      )
    },
    {
      id: 'transaction-handling',
      title: 'Transaction Handling',
      icon: <RefreshCw className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              Communication with the PayDRC processor API follows an <strong>asynchronous communication model</strong>.
            </p>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              In this model, the initiator sends a request to the API, which returns a response that is not the final status of the transaction. The initial response from the PayDRC API is merely an acknowledgment to indicate that the money transfer request has been received.
            </p>
            
            <p className="text-gray-700 leading-relaxed">
              However, to obtain the final status of the transaction, the initiator must perform a check using an appropriate search action via the same API, but with a slightly different parameter format. This allows the initiator to retrieve the updated status of the transaction.
            </p>
            
            <p className="text-gray-700 leading-relaxed">
              Additionally, in addition to transaction checking, final status notifications can also be sent to the initiator via a callback mechanism. When making a transaction request, you can specify a callback URL in the request parameters. Once the final status of the transaction is available, PayDRC will send a notification to this callback URL with the transaction details, including the final status.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'basic-flow',
      title: 'Basic Flow',
      icon: <ArrowRight className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Initiator</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Client</li>
                  <li>• Payment Transaction</li>
                  <li>• Acknowledge Request</li>
                  <li>• Lifetime Query Transaction Status</li>
                  <li>• Final Status</li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">PayDRC API</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Request Fund Transfer</li>
                  <li>• Acknowledge Request</li>
                  <li>• Transaction Update</li>
                  <li>• SWITCH</li>
                  <li>• Process Request</li>
                  <li>• Computed Response</li>
                  <li>• Callback</li>
                </ul>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">GATEWAY Platform</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Vodacom</li>
                  <li>• Airtel</li>
                  <li>• Orange</li>
                  <li>• Africell</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'payin-payout',
      title: 'PayIn and PayOut API',
      icon: <Code className="h-5 w-5" />,
      content: (
        <div className="space-y-8">
          {/* Parameters Section */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">3.1. Parameters</h3>
            <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 font-medium text-gray-700">Parameter</th>
                    <th className="text-left py-2 font-medium text-gray-700">Type</th>
                    <th className="text-left py-2 font-medium text-gray-700">Required</th>
                    <th className="text-left py-2 font-medium text-gray-700">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="py-2 font-mono text-blue-600">merchant_id</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">Your Moko Afrika merchant ID</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">merchant_secrete</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">Your Moko Afrika merchant secret key</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">firstname</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">Your Moko Afrika first name</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">lastname</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">Your Moko Afrika last name</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">email</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">Your Moko Afrika email address</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">amount</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">The transaction amount</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">currency</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">The currency of the transaction</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">action</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">The action to perform (debit for payin and credit for payout)</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">customer_number</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">The customer's phone number</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">reference</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">A unique reference for the transaction</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">method</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Required</td>
                    <td className="py-2">The payment method to use (e.g., airtel, mtn, etc.)</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-blue-600">callback_url</td>
                    <td className="py-2">string</td>
                    <td className="py-2">Optional</td>
                    <td className="py-2">The URL where Moko Afrikawill send transaction notifications</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* PayIn API Section */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">3.2. PayIn (C2B) API</h3>
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">Customer-to-Business Payment Collection via Mobile Money</h4>
                <p className="text-green-700 text-sm">
                  This API enables merchants to securely collect payments directly from a customer's mobile money wallet, with transaction authorization via SMS/Push notification.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Key Features</h5>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Push Payment Authorization</li>
                    <li>• Real-Time Notifications</li>
                    <li>• Multi-Network Support</li>
                    <li>• Multi-currency support (CDF, USD)</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Example Use Cases</h5>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Bill payments (utilities, taxes, insurance)</li>
                    <li>• E-commerce checkouts</li>
                    <li>• Service subscriptions</li>
                    <li>• In-store POS payments</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4 text-sm">
                  <div className="flex-1">
                    <span className="font-semibold">TEST Endpoint:</span>
                    <div className="font-mono text-blue-600 mt-1 break-all">POST https://api.gofreshpay.com/api/v1/gateway</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold">PROD Endpoint:</span>
                    <div className="font-mono text-blue-600 mt-1 break-all">POST https://paydrc.gofreshbakery.net/api/v5/</div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Example Request:</span>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(payinRequest, null, 2), 'payin-request')}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'payin-request' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'payin-request' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm whitespace-pre-wrap break-words">
{JSON.stringify(payinRequest, null, 2)}
                    </pre>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Example Response:</span>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(payinResponse, null, 2), 'payin-response')}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'payin-response' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'payin-response' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm whitespace-pre-wrap break-words">
{JSON.stringify(payinResponse, null, 2)}
                    </pre>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 text-sm">
                    <strong>Note:</strong> The <code>"Status": "Success"</code> in this response only confirms Moko Afrikareceived the request. The actual transaction status (Submitted/Pending/Failed/Successful) is available via Check Status API or Callback.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* PayOut API Section */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">3.3. PayOut (B2C) API</h3>
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-2">Business-to-Customer Instant Mobile Money Transfers</h4>
                <p className="text-purple-700 text-sm">
                  This API enables merchants to send instant payments directly to customers' mobile wallets without requiring recipient PIN validation.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Key Features</h5>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Instant Transfers</li>
                    <li>• Comprehensive Tracking</li>
                    <li>• Multi-Operator Support</li>
                    <li>• PIN-less transactions</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Typical Use Cases</h5>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Salary payments</li>
                    <li>• Cashback and refunds</li>
                    <li>• Vendor payments</li>
                    <li>• Loan disbursements</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Example Request:</span>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(payoutRequest, null, 2), 'payout-request')}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'payout-request' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'payout-request' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm whitespace-pre-wrap break-words">
{JSON.stringify(payoutRequest, null, 2)}
                    </pre>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Example Response:</span>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(payoutResponse, null, 2), 'payout-response')}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'payout-response' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'payout-response' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-sm whitespace-pre-wrap break-words">
{JSON.stringify(payoutResponse, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'check-status',
      title: 'Check Status API',
      icon: <RefreshCw className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Real-Time Tracking</h4>
              <p className="text-blue-700 text-sm">Check the current status of transactions (pending, success, failed)</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">Comprehensive Monitoring</h4>
              <p className="text-green-700 text-sm">Retrieve detailed transaction updates and error messages</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">Seamless Integration</h4>
              <p className="text-purple-700 text-sm">Simple API calls to verify transactions using unique reference</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <div className="flex-1">
                <span className="font-semibold">TEST Endpoint:</span>
                <div className="font-mono text-blue-600 mt-1 break-all">POST https://api.gofreshpay.com/api/v1/gateway</div>
              </div>
              <div className="flex-1">
                <span className="font-semibold">PROD Endpoint:</span>
                <div className="font-mono text-blue-600 mt-1 break-all">POST https://paydrc.gofreshbakery.net/api/v5/</div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">Example Request:</span>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(checkStatusRequest, null, 2), 'check-status-request')}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  {copiedCode === 'check-status-request' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copiedCode === 'check-status-request' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm whitespace-pre-wrap break-words">
{JSON.stringify(checkStatusRequest, null, 2)}
                </pre>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">Example Response:</span>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(checkStatusResponse, null, 2), 'check-status-response')}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  {copiedCode === 'check-status-response' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copiedCode === 'check-status-response' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm whitespace-pre-wrap break-words">
{JSON.stringify(checkStatusResponse, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'callback',
      title: 'Callback Implementation',
      icon: <Shield className="h-5 w-5" />,
      content: (
        <div className="space-y-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800">
              Moko Afrikaprovides real-time transaction status updates through <strong>secure HTTP POST callbacks</strong> to the merchant's specified callback_url.
            </p>
          </div>

          {/* Security Features */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Security Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">AES-256-CBC Encryption</h4>
                <p className="text-red-700 text-sm">Protects sensitive data during transmission</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">HMAC-SHA256 Signature</h4>
                <p className="text-green-700 text-sm">Verifies message authenticity and integrity</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-2">IP Whitelisting</h4>
                <p className="text-purple-700 text-sm">Restricts callback access to trusted servers</p>
              </div>
            </div>
          </div>

          {/* Implementation Code Samples */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Implementation Examples</h3>
            <div className="space-y-6">
              {codeExamples.map((example, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-800 text-white px-4 py-2 flex justify-between items-center">
                    <span className="font-mono text-sm">{example.language}</span>
                    <button
                      onClick={() => copyToClipboard(example.code, example.language)}
                      className="flex items-center text-sm hover:text-gray-300"
                    >
                      {copiedCode === example.language ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === example.language ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-gray-900 text-gray-100 p-4 overflow-x-auto">
                    <pre className="text-sm whitespace-pre-wrap break-words">
{example.code}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'transaction-flow',
      title: 'Transaction Status Flow',
      icon: <ArrowRight className="h-5 w-5" />,
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">Initial Response</h4>
              <ul className="text-yellow-700 text-sm space-y-1">
                <li>• "Status": "Success" confirms receipt by FreshPay</li>
                <li>• Actual status is "Submitted" (processing begins)</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">Final Status</h4>
              <ul className="text-green-700 text-sm space-y-1">
                <li>• Callback Notifications (Failed/Successful)</li>
                <li>• Check Status API (All statuses)</li>
              </ul>
            </div>
          </div>

          {/* Response Parameters Table */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Key Response Parameters</h4>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 border-b">Parameter</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 border-b">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-3 px-4 font-mono text-blue-600">Trans_Status</td>
                      <td className="py-3 px-4">Final transaction status (Failed/Successful in callbacks; includes intermediates in Check Status API)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-mono text-blue-600">Transaction_id</td>
                      <td className="py-3 px-4">FreshPay's internal transaction reference</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-mono text-blue-600">Financial_Institution_id</td>
                      <td className="py-3 px-4">Mobile money provider's transaction reference</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-mono text-blue-600">Reference</td>
                      <td className="py-3 px-4">Merchant's original transaction reference</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-mono text-blue-600">Trans_Status_Description</td>
                      <td className="py-3 px-4">Detailed failure reason (if applicable)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Callback vs Check Status */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Callback vs Check Status API</h4>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 border-b">Feature</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 border-b">Callback</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700 border-b">Check Status API</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-3 px-4 font-medium">Trigger</td>
                      <td className="py-3 px-4">Automatic (FreshPay-initiated)</td>
                      <td className="py-3 px-4">Manual (Merchant-initiated)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-medium">Statuses</td>
                      <td className="py-3 px-4">Only Failed/Successful</td>
                      <td className="py-3 px-4">All (Submitted, Pending, Failed, Successful)</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-medium">Use Case</td>
                      <td className="py-3 px-4">Real-time final updates</td>
                      <td className="py-3 px-4">On-demand status checks</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const filteredSections = documentationSections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.content.props.children.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-[#63d400] p-2 rounded-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Moko AfrikaAPI Documentation</h1>
                <p className="text-gray-600">Complete integration guide for PayDRC processor</p>
              </div>
            </div>
            <button className="flex items-center px-4 py-2 bg-[#63d400] text-white rounded-lg hover:bg-[#58c400] transition-colors duration-200">
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 sticky top-24">
              {/* Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#63d400] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Navigation */}
              <nav className="p-4">
                <ul className="space-y-2">
                  {documentationSections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className="flex items-center space-x-3 px-3 py-2 text-gray-700 hover:text-[#63d400] hover:bg-gray-50 rounded-lg transition-colors duration-200"
                      >
                        {section.icon}
                        <span className="font-medium">{section.title}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0"> {/* Added min-w-0 for proper flexbox constraint */}
            <div className="space-y-8">
              {filteredSections.length > 0 ? (
                filteredSections.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                  >
                    <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-6 py-4">
                      <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        {section.icon}
                        <span className="ml-3">{section.title}</span>
                      </h2>
                    </div>
                    <div className="p-6 overflow-hidden"> {/* Added overflow-hidden */}
                      {section.content}
                    </div>
                  </section>
                ))
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                  <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
                  <p className="text-gray-600">Try adjusting your search terms</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;